import { appelApi } from '../api/client';
import { lireJeton } from '../session';
import type { SignalementErreur } from '@colette/shared';

// Le journal des erreurs de la PWA (spec observation-usage) : ce qui casse dans le
// navigateur d'un testeur est signale au serveur, sinon un ecran blanc chez Marianne
// ne laisse aucune trace nulle part.
//
// Regle d'or : le journal ne cree JAMAIS de probleme. Tout est enveloppe de try/catch,
// un echec d'envoi est silencieux, et deux garde-fous evitent la tempete : au plus dix
// envois par chargement de page, et jamais deux fois le meme message d'affilee.

const MAX_ENVOIS_PAR_PAGE = 10;

interface DependancesJournal {
  envoyer?: (signalement: SignalementErreur) => Promise<void>;
  jetonPresent?: () => boolean;
}

async function envoiReel(signalement: SignalementErreur): Promise<void> {
  await appelApi('/journal-erreurs', { methode: 'POST', corps: signalement });
}

export function creerJournalErreurs(dependances: DependancesJournal = {}) {
  const envoyer = dependances.envoyer ?? envoiReel;
  const jetonPresent = dependances.jetonPresent ?? (() => lireJeton() !== null);
  let envois = 0;
  let dernierMessage: string | null = null;

  function signaler(message: string, stack: string | null): void {
    try {
      if (!jetonPresent()) return;
      if (envois >= MAX_ENVOIS_PAR_PAGE) return;
      if (message === dernierMessage) return;
      envois += 1;
      dernierMessage = message;
      const signalement: SignalementErreur = {
        message: message.slice(0, 500),
        stack: stack === null ? null : stack.slice(0, 4000),
        ecran: window.location.pathname.slice(0, 200),
        userAgent: navigator.userAgent.slice(0, 300),
      };
      void envoyer(signalement).catch(() => {
        // L'echec est silencieux : perdre un signalement vaut mieux qu'une boucle.
      });
    } catch {
      // Jamais d'erreur depuis le journal lui-meme.
    }
  }

  function installer(): void {
    window.addEventListener('error', (evenement) => {
      const erreur = (evenement as ErrorEvent).error as unknown;
      signaler(
        String((evenement as ErrorEvent).message ?? 'Erreur inconnue'),
        erreur instanceof Error ? (erreur.stack ?? null) : null,
      );
    });
    window.addEventListener('unhandledrejection', (evenement) => {
      const raison = (evenement as { reason?: unknown }).reason;
      signaler(
        raison instanceof Error ? raison.message : String(raison ?? 'Promesse rejetee'),
        raison instanceof Error ? (raison.stack ?? null) : null,
      );
    });
  }

  return { signaler, installer };
}
